import validAcessRoute from './validAcessRoute'
import { canAccessAction } from './permissions'
import {AccessLevel} from "@views/configurations/enums/accessLevel";

/**
 * Helper modular para controle de permissões
 * Suporta tanto o sistema antigo (adm_roles) quanto o novo (screen_action_permission)
 */
export default class PermissionHelper {
  constructor(store) {
      this.store = store;
      const safeGetters = (store && store.getters) ? store.getters : {};
      const profile = safeGetters.getProfile || {};
      this.profile = profile;
      this.useNewPermissions = !!profile.use_action_permissions;
  }

  /**
   * Verifica se o usuário tem permissão para acessar uma tela/ação
   * @param {string|Array} permission - Nome da permissão ou array de roles antigas
   * @param {string} requiredLevel - Nível necessário para novo sistema (reader, editor)
   * @returns {boolean}
   */
  hasPermission(permission, requiredLevel = AccessLevel.READER) {
    // Se está usando o novo sistema de permissões
    if (this.useNewPermissions) {
      return this.hasNewPermission(permission, requiredLevel)
    }
    
    // Fallback para sistema antigo
    return this.hasOldPermission(permission)
  }

  /**
   * Verifica permissão no novo sistema (screen_action_permission)
   * @param {string} actionName - Nome da ação
   * @param {string} requiredLevel - Nível necessário
   * @returns {boolean}
   */
  hasNewPermission(actionName, requiredLevel = AccessLevel.READER) {
    return canAccessAction(this.store, actionName, requiredLevel)
  }

  /**
   * Verifica permissão no sistema antigo (adm_roles)
   * @param {Array} roleNames - Array de nomes de roles
   * @returns {boolean}
   */
  hasOldPermission(roleNames) {
    if (typeof roleNames === 'string') {
      roleNames = [roleNames]
    }

    return validAcessRoute(roleNames, this.profile) > 0
  }

  /**
   * Mapeamento de telas para permissões
   * Facilita a migração gradual do sistema antigo para o novo
   */
  static SCREEN_PERMISSIONS = {
    // Navegação principal
    dashboard: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'attendance', 'risk', 'marketing', 'success', 'financialAssistant', 'greennSalesAssistent', 'operations', 'operations_viewer'],
      new: 'dashboard_show_screen'
    },
    
    operations: {
      old: ['operations'],
      new: 'operations_show_screen'
    },

    operations_viewer: {
      old: ['operations_viewer'],
      new: 'operations_viewer_show_screen'
    },
    
    clientes: {
      old: ['greennSalesAssistent', 'administrator', 'financial', 'financialManager', 'compliance', 'attendance', 'risk', 'success', 'financialAssistant'],
      new: 'clients_show_screen'
    },
    
    vendas: {
      old: ['administrator', 'financial', 'financialManager', 'risk', 'compliance', 'attendance', 'success', 'financialAssistant'],
      new: 'sales_show_screen'
    },
    
    chargebacks: {
      old: ['administrator', 'financial', 'financialManager', 'compliance'],
      new: 'chargebacks_show_screen'
    },
    
    antecipacao: {
      old: ['administrator', 'financial', 'financialManager', 'risk', 'compliance', 'financialAssistant'],
      new: 'anticipation_show_screen'
    },
    
    saques: {
      old: ['administrator', 'financial', 'financialManager', 'financialAssistant'],
      new: 'withdrawals_show_screen'
    },
    
    reembolsos: {
      old: ['administrator', 'financial', 'financialManager', 'financialAssistant', 'support'],
      new: 'refunds_show_screen'
    },
    
    produtos: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'risk', 'attendance', 'success', 'financialAssistant'],
      new: 'products_show_screen'
    },
    
    conciliacaoVendas: {
      old: ['administrator', 'financial', 'financialManager'],
      new: 'sales_reconciliation_show_screen'
    },
    
    conciliacaoAssinatura: {
      old: ['administrator', 'financial', 'financialManager'],
      new: 'subscription_reconciliation_show_screen'
    },
    
    revisaoDocumentos: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'risk', 'financialAssistant'],
      new: 'document_review_show_screen'
    },
    
    analises: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'risk'],
      new: 'risk_analysis_show_screen'
    },
    
    identidade: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'risk', 'financialAssistant'],
      new: 'identity_validation_show_screen'
    },
    
    reclamacao: {
      old: ['administrator', 'financial', 'financialManager', 'attendance', 'compliance', 'risk', 'success'],
      new: 'claims_show_screen'
    },
    
    marcos: {
      old: ['administrator', 'financial', 'financialManager', 'success'],
      new: 'milestones_show_screen'
    },
    
    cashback: {
      old: ['administrator', 'financial', 'financialManager'],
      new: 'cashback_show_screen'
    },
    
    banners: {
      old: ['marketing', 'administrator'],
      new: 'banners_show_screen'
    },
    
    // Rotas administrativas (não estão no side-nav)
    requisicoes: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'risk'],
      new: 'requests_show_screen'
    },
    
    analiseDeVenda: {
      old: ['administrator', 'financial', 'financialManager', 'compliance', 'risk', 'financialAssistant'],
      new: 'sale_analysis_show_screen'
    },
    
    configurations: {
      old: ['administrator'],
      new: 'configurations_show_screen'
    },
    xGrow: {
      old: ['administrator', 'financial', 'financialManager', 'financialAssistant'],
      new: 'x_grow_show_screen'
    }
  }

  /**
   * Verifica permissão usando o mapeamento de telas
   * @param {string} screenName - Nome da tela
   * @param {string} requiredLevel - Nível necessário para novo sistema
   * @returns {boolean}
   */
  canAccessScreen(screenName, requiredLevel = AccessLevel.READER) {
    const screenConfig = PermissionHelper.SCREEN_PERMISSIONS[screenName]
    if (!screenConfig) {
      console.warn(`Configuração de permissão não encontrada para tela: ${screenName}`)
      return false
    }

    // Configurações só existe para quem está na feature flag de permissões novas
    if (screenName === 'configurations' && !this.useNewPermissions) {
      return false
    }

    // Se está usando o novo sistema
    if (this.useNewPermissions) {
      return this.hasNewPermission(screenConfig.new, requiredLevel)
    }
    
    // Fallback para sistema antigo
    return this.hasOldPermission(screenConfig.old)
  }

  /**
   * Método utilitário para Vue components
   * Retorna um objeto com todas as permissões calculadas
   */
  getScreenPermissions() {
    const permissions = {}
    
    Object.keys(PermissionHelper.SCREEN_PERMISSIONS).forEach(screenName => {
      permissions[screenName] = this.canAccessScreen(screenName)
    })
    
    return permissions
  }
}
